import {
  HomeLocaleSelector,
  getLocalLanguage,
  getNavigatorLanguage,
  getSafeUrl,
  useAppDispatch,
  useAppSelector,
  useGetSystemDataQuery,
  useUserSessionCheckQuery
} from '@monorepo/shared';
import './Home.scss';
import { EnvConst, host, server } from '../../config/const';
import { forwardRef, useEffect, useState } from 'react';
import { DOMHelper, Dropdown, Stack } from 'rsuite';
import { Icon } from '@rsuite/icons';
import DropdownItem from 'rsuite/esm/Dropdown/DropdownItem';
import DropdownSeparator from 'rsuite/esm/Dropdown/DropdownSeparator';
import ArrowRightLineIcon from '@rsuite/icons/ArrowRightLine';
import { MainSwiper } from './MainContent';
import { FiExternalLink } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import {
  selectCurrentLocale,
  updateLocale
} from '@monorepo/shared/store/authSlice';

export const Home = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { getWidth, on } = DOMHelper;
  const [mousewheelEnable, setMousewheelEnable] = useState<boolean>(true);
  const [swiperDirection, setSwiperDirection] = useState<
    'vertical' | 'horizontal' | undefined
  >('vertical');
  const [windowWidth, setWindowWidth] = useState<number>(getWidth(window));
  const { data: sessionData } = useUserSessionCheckQuery({
    server: server
  });
  const { data: systemData } = useGetSystemDataQuery({
    server: server
  });
  const currentLocale: string = useAppSelector(selectCurrentLocale);
  const login = () => {
    if (window) {
      window.open(
        getSafeUrl(
          `${EnvConst.auth_host}/ssologin/?next=${host}/login-redirect`
        ),
        '_self'
      );
    }
  };
  useEffect(() => {
    const userLocale = getLocalLanguage() || 'auto';
    const locale = userLocale === 'auto' ? getNavigatorLanguage() : userLocale;
    if (locale !== currentLocale) {
      dispatch(updateLocale({ key: userLocale, locale: locale }));
    }
  }, []);

  useEffect(() => {
    setWindowWidth(getWidth(window));
    const resizeListener = on(window, 'resize', () =>
      setWindowWidth(getWidth(window))
    );
    return () => {
      resizeListener.off();
    };
  }, []);

  useEffect(() => {
    if (windowWidth <= 991) {
      setSwiperDirection('horizontal');
      setMousewheelEnable(false);
    } else {
      setSwiperDirection('vertical');
      setMousewheelEnable(true);
    }
  }, [windowWidth]);

  useEffect(() => {
    if (sessionData?.active) {
      window.open(getSafeUrl(`${host}/login-redirect`), '_self');
    }
  }, [sessionData]);

  return (
    <div className="land-main">
      <div className="land-left">
        <div className="land-head">
          <div className="head-left">
            <div className="head-logo"></div>
          </div>
          <>
            <Dropdown
              className="land-dropdown"
              trigger="hover"
              placement="bottomEnd"
              renderToggle={() => <div className="head-icon doc"></div>}
            >
              <div className="land-dropdown-title">
                {t('faz.home.resources')}
              </div>

              <DropdownSeparator style={{ margin: '0' }} />
              <DropdownItem
                className="link-item"
                as="a"
                target="_blank"
                href="https://docs.fortinet.com/document/fortianalyzer/7.4.0/best-practices/945453/overview"
              >
                <Stack spacing={6} alignItems="center">
                  <Stack.Item>Best Practices</Stack.Item>
                  <FiExternalLink />
                </Stack>
              </DropdownItem>
              <DropdownItem
                className="link-item"
                as="a"
                target="_blank"
                href="https://docs.fortinet.com/document/fortianalyzer/7.4.0/examples/110418/introduction"
              >
                <Stack spacing={6} alignItems="center">
                  <Stack.Item>Examples</Stack.Item>
                  <FiExternalLink />
                </Stack>
              </DropdownItem>
            </Dropdown>
            <Dropdown
              className="land-dropdown"
              trigger="hover"
              placement="bottomEnd"
              renderToggle={() => <div className="head-icon status"></div>}
            >
              <div className="land-dropdown-title">
                {t('faz.home.portalStatus')}
              </div>
              <DropdownSeparator style={{ margin: '0' }} />
              <DropdownItem
                as="a"
                target="_blank"
                href="https://status.fortianalyzer.forticloud.com/"
              >
                {t('faz.home.portalStatusText')}
                <ArrowRightLineIcon />
              </DropdownItem>
            </Dropdown>
            <HomeLocaleSelector
              host={host}
              renderToggle={() => (
                <div className="head-icon international"></div>
              )}
            />
          </>
        </div>
        <div className="land-icon"></div>
        <div className="land-title">FortiAnalyzer Cloud</div>
        <div className="login-btn" onClick={() => login()}>
          <Icon
            as={LoginIcon}
            style={{
              color: 'white',
              width: '2.22vh',
              height: '2.22vh',
              marginRight: '2.22vh'
            }}
          />
          <span>
            {t('faz.home.login')} / {t('faz.home.createAccount')}
          </span>
        </div>
        <div className="land-message">
          <p>{t('faz.home.description')}</p>
          {systemData?.discover_more && (
            <a
              style={{ color: '#236093' }}
              target="_blank"
              href={systemData?.discover_more}
            >
              <span>{t('faz.home.discoverMore')}</span>
              <span>&nbsp;&gt;</span>
            </a>
          )}
          {/* <a
            style={{ color: '#236093' }}
            target="_blank"
            href="https://docs.fortinet.com/product/fortianalyzer/7.6"
          >
            {t('faz.home.discoverMore')}
            &gt;
          </a> */}
        </div>
        <a
          className="land-fortinet-logo"
          href="https://fortinet.com"
          target="_blank"
        ></a>
        <div className="land-version">{systemData?.portal_version}</div>
      </div>
      <div className="land-right">
        <MainSwiper
          enableMouse={mousewheelEnable}
          direction={swiperDirection}
        />
      </div>
    </div>
  );
};

const LoginIcon = forwardRef<SVGSVGElement>((props, ref) => (
  <svg
    {...props}
    width="18px"
    height="18px"
    fill="currentColor"
    viewBox="0 0 18 18"
    ref={ref}
  >
    <path d="M14 15h-2.625a.376.376 0 0 1-.375-.375v-1.25c0-.206.169-.375.375-.375H14c.553 0 1-.447 1-1V6c0-.553-.447-1-1-1h-2.625A.376.376 0 0 1 11 4.625v-1.25c0-.206.169-.375.375-.375H14a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3zm-1.469-6.281-5.25-5.25C6.813 3 6 3.329 6 4v3H1.75a.748.748 0 0 0-.75.75v3c0 .416.334.75.75.75H6v3c0 .672.813 1 1.281.531l5.25-5.25a.756.756 0 0 0 0-1.062z"></path>
  </svg>
));
